import React, { useEffect, useState, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTicketStore } from "../../../store/ticketStore";
import ImageEditor from "tui-image-editor";
import "tui-image-editor/dist/tui-image-editor.css";

// tui-image-editor 불필요한 UI 숨기기 CSS
const hideButtonsStyle = `
	.tui-image-editor-header-buttons .tui-image-editor-download-btn,
	.tui-image-editor-header-buttons .tui-image-editor-load-btn,
	.tui-image-editor-submenu,
	.tui-image-editor-menu {
		display: none !important;
	}
`;

// 이미지 에디터 테마 설정 (autoseat 레거시 설정 기반)
const blackTheme = {
	"common.bi.image": "",
	"common.bisize.width": "0px",
	"common.bisize.height": "0px",
	"common.backgroundImage": "none",
	"common.backgroundColor": "#1e1e1e",
	"common.border": "0px",

	// header
	"header.backgroundImage": "none",
	"header.backgroundColor": "transparent",
	"header.border": "0px",

	// load button - 숨김
	"loadButton.backgroundColor": "#fff",
	"loadButton.border": "1px solid #ddd",
	"loadButton.color": "#222",
	"loadButton.fontFamily": "'Noto Sans', sans-serif",
	"loadButton.fontSize": "12px",
	"loadButton.display": "none",

	// download button - 숨김
	"downloadButton.backgroundColor": "#fdba3b",
	"downloadButton.border": "1px solid #fdba3b",
	"downloadButton.color": "#fff",
	"downloadButton.fontFamily": "'Noto Sans', sans-serif",
	"downloadButton.fontSize": "12px",
	"downloadButton.display": "none",

	// main icons
	"menu.normalIcon.color": "#8a8a8a",
	"menu.activeIcon.color": "#555555",
	"menu.disabledIcon.color": "#434343",
	"menu.hoverIcon.color": "#e9e9e9",
	"menu.iconSize.width": "24px",
	"menu.iconSize.height": "24px",

	// submenu icons
	"submenu.normalIcon.color": "#8a8a8a",
	"submenu.activeIcon.color": "#e9e9e9",
	"submenu.iconSize.width": "32px",
	"submenu.iconSize.height": "32px",

	// submenu primary color
	"submenu.backgroundColor": "#1e1e1e",
	"submenu.partition.color": "#3c3c3c",

	// submenu labels
	"submenu.normalLabel.color": "#8a8a8a",
	"submenu.normalLabel.fontWeight": "lighter",
	"submenu.activeLabel.color": "#fff",
	"submenu.activeLabel.fontWeight": "lighter",

	// checkbox style
	"checkbox.border": "0px",
	"checkbox.backgroundColor": "#fff",

	// range style
	"range.pointer.color": "#fff",
	"range.bar.color": "#666",
	"range.subbar.color": "#d1d1d1",
	"range.display": "none",
	"range.disabledPointer.color": "#414141",
	"range.disabledBar.color": "#282828",
	"range.disabledSubbar.color": "#414141",
	"range.value.color": "#fff",
	"range.value.fontWeight": "lighter",
	"range.value.fontSize": "11px",
	"range.value.border": "1px solid #353535",
	"range.value.backgroundColor": "#151515",
	"range.title.color": "#fff",
	"range.title.fontWeight": "lighter",

	// colorpicker style
	"colorpicker.button.border": "1px solid #1e1e1e",
	"colorpicker.title.color": "#fff",
};

export default function MaskingModal({
	isOpen,
	onClose,
	ticketId,
	imageUrl,
	tickets = [],
	currentIndex = 0,
	onChangeTicket,
}) {
	const [masks, setMasks] = useState([]);
	const editorRef = useRef(null);
	const imageEditorRef = useRef(null);

	// Zustand 스토어에서 마스크 관련 함수 가져오기
	const { loadMaskData, saveMaskData } = useTicketStore();

	// CSS 스타일 주입 (Load/Download 버튼 숨기기)
	useEffect(() => {
		const styleElement = document.createElement("style");
		styleElement.textContent = hideButtonsStyle;
		document.head.appendChild(styleElement);
		return () => {
			document.head.removeChild(styleElement);
		};
	}, []);

	// 이미지 에디터 초기화
	useEffect(() => {
		if (isOpen && imageUrl && editorRef.current) {
			const url = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}_=${Date.now()}`;
			try {
				const imageEditor = new ImageEditor(editorRef.current, {
					includeUI: {
						loadImage: {
							path: url,
							name: "Ticket Image",
						},
						theme: blackTheme,
						menu: ["shape"],
						uiSize: {
							width: "100%",
							height: "700px",
						},
						menuBarPosition: "bottom",
					},
					cssMaxWidth: 1200,
					cssMaxHeight: 900,
					usageStatistics: false,
					selectionStyle: {
						cornerStyle: "rect",
						cornerSize: 25,
						cornerColor: "#FF0000",
						cornerStrokeColor: "#FF0000",
						transparentCorners: false,
						lineWidth: 2,
						borderColor: "#fff",
					},
				});

				imageEditorRef.current = imageEditor;

				// 마스크 로드
				if (ticketId) {
					setTimeout(() => {
						handleLoadMasks(ticketId);
					}, 1000);
				}
			} catch (error) {
				console.error("이미지 에디터 초기화 중 오류 발생:", error);
			}
		}

		return () => {
			// 컴포넌트 언마운트 시 에디터 정리
			if (imageEditorRef.current) {
				try {
					imageEditorRef.current.destroy();
					imageEditorRef.current = null;
				} catch (error) {
					console.error("이미지 에디터 정리 중 오류 발생:", error);
				}
			}
		};
	}, [isOpen, imageUrl, ticketId]);

	// 마스크 로드 함수
	const handleLoadMasks = async (ticketId) => {
		try {
			// ticketStore에서 마스크 데이터 가져오기
			const maskData = await loadMaskData(ticketId);
			if (maskData && maskData.length > 0) {
				setMasks(maskData);

				// 마스크 순차적으로 적용
				addAllMasksAtOnce(maskData);
			}
		} catch (error) {
			console.error("마스크 로드 중 오류 발생:", error);
		}
	};

	// 마스크 순차적으로 추가하는 함수
	const addAllMasksAtOnce = (masks) => {
		if (!imageEditorRef.current || !masks || masks.length === 0) return;

		// 각 마스크를 시간 간격을 두고 순차적으로 추가
		masks.forEach((mask, index) => {
			setTimeout(() => {
				try {
					imageEditorRef.current.addShape("rect", {
						left: mask.x,
						top: mask.y,
						width: mask.width,
						height: mask.height,
						fill: "white",
						stroke: "orange",
						strokeWidth: 5,
					});
				} catch (error) {
					console.error(`마스크 ${index + 1} 추가 실패:`, error);
				}
			}, (index + 1) * 10); // 각 마스크마다 1초씩 지연 시간 증가
		});
	};

	// 마스크 저장 함수
	const handleSaveMasks = async () => {
		if (!imageEditorRef.current || !ticketId) return;

		try {
			// 이미지 에디터에서 사각형 객체 가져오기
			const canvas = imageEditorRef.current._graphics.getCanvas();
			const objects = canvas.getObjects();

			// 사각형 객체만 필터링하여 마스크 데이터 생성
			const maskData = objects
				.filter((obj) => obj.type === "rect")
				.map((obj) => ({
					x: obj.left,
					y: obj.top,
					width: obj.width,
					height: obj.height,
				}));

			// ticketStore를 통해 마스크 데이터 저장
			const result = await saveMaskData(ticketId, maskData);

			// 결과 알림
			alert(result.message);
		} catch (error) {
			console.error("마스크 저장 중 오류 발생:", error);
			alert("마스크 저장 중 오류가 발생했습니다. 다시 시도해주세요.");
		}
	};

	// 사각형 마스크 추가
	const addRectangle = () => {
		if (!imageEditorRef.current) return;

		imageEditorRef.current.addShape("rect", {
			left: 200,
			top: 200,
			width: 500,
			height: 500,
			fill: "white",
			stroke: "orange",
			strokeWidth: 5,
		});
	};

	// 선택된 객체 삭제
	const removeActiveObject = () => {
		if (!imageEditorRef.current) return;

		const canvas = imageEditorRef.current._graphics.getCanvas();
		const activeObject = canvas.getActiveObject();

		if (activeObject) {
			canvas.remove(activeObject);
			canvas.renderAll();
		}
	};

	// 모든 객체 삭제
	const clearObjects = () => {
		if (!imageEditorRef.current) return;

		const canvas = imageEditorRef.current._graphics.getCanvas();
		const objects = canvas.getObjects();

		// 사각형 객체만 삭제
		objects
			.filter((obj) => obj.type === "rect")
			.forEach((obj) => {
				canvas.remove(obj);
			});

		canvas.renderAll();
	};

	// 이전/다음 티켓 이동
	const navigateTicket = (direction) => {
		if (!tickets || tickets.length === 0 || !onChangeTicket) return;

		const ticketArray = tickets[0]; // OCR 응답 구조에 맞게 접근
		if (!ticketArray || ticketArray.length <= 1) return;

		let newIndex = currentIndex;

		if (direction === "prev") {
			if (currentIndex > 0) {
				newIndex = currentIndex - 1;
			}
		} else {
			if (currentIndex < ticketArray.length - 1) {
				newIndex = currentIndex + 1;
			}
		}

		if (newIndex !== currentIndex) {
			onChangeTicket(newIndex);
		}
	};

	// 네비게이션 버튼 상태 업데이트
	const updateNavigationButtons = () => {
		const ticketArray = tickets[0] || [];
		const prevDisabled = currentIndex <= 0;
		const nextDisabled = currentIndex >= ticketArray.length - 1;

		return { prevDisabled, nextDisabled };
	};

	if (!isOpen) return null;

	const { prevDisabled, nextDisabled } = updateNavigationButtons();

	// 모달 바깥 클릭 핸들러
	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<div
			className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
			onClick={handleBackdropClick}
		>
			<div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
				<div className="flex justify-between items-center border-b p-4">
					<h3 className="text-lg font-semibold">티켓 마스킹</h3>
					<button
						onClick={onClose}
						className="text-gray-500 hover:text-gray-700"
					>
						<XMarkIcon className="h-6 w-6" />
					</button>
				</div>

				<div className="p-4 flex flex-col flex-grow overflow-hidden">
					<div className="flex justify-center space-x-4 mb-4">
						<button
							className={`px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 ${
								prevDisabled
									? "opacity-50 cursor-not-allowed"
									: ""
							}`}
							onClick={() => navigateTicket("prev")}
							disabled={prevDisabled}
						>
							&lt;&lt; 이전
						</button>
						<button
							className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
							onClick={addRectangle}
						>
							마스킹 추가
						</button>
						<button
							className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
							onClick={removeActiveObject}
						>
							선택된 마스킹 삭제
						</button>
						<button
							className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
							onClick={clearObjects}
						>
							모든 마스킹 삭제
						</button>
						<button
							className={`px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 ${
								nextDisabled
									? "opacity-50 cursor-not-allowed"
									: ""
							}`}
							onClick={() => navigateTicket("next")}
							disabled={nextDisabled}
						>
							다음 &gt;&gt;
						</button>
					</div>

					<div
						id="tui-image-editor-container"
						ref={editorRef}
						className="border rounded-lg flex-grow min-h-0 flex items-center justify-center bg-gray-100"
					>
						{!imageUrl && (
							<p className="text-gray-500">
								티켓이 저장되지 않았습니다
							</p>
						)}
					</div>
				</div>

				<div className="border-t p-4 flex justify-end space-x-2">
					<button
						onClick={onClose}
						className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
					>
						닫기
					</button>
					<button
						className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
						onClick={handleSaveMasks}
						disabled={!ticketId}
					>
						마스크 저장
					</button>
				</div>
			</div>
		</div>
	);
}
